import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';

export function MfaChallengePage() {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const navigate = useNavigate();
  const fetchSession = useAuthStore((s) => s.fetchSession);
  const [submitting, setSubmitting] = useState(false);

  const schema = useMemo(
    () =>
      z.object({
        code: z.string().regex(/^\d{6}$/, t('mfa.codeInvalid')),
      }),
    [t],
  );

  type Input = z.infer<typeof schema>;

  const state = (location.state as { email?: string; session?: string } | null) ?? {};
  const { email = '', session = '' } = state;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Input>({ resolver: zodResolver(schema) });

  if (!email || !session) {
    navigate('/giris');
    return null;
  }

  async function onSubmit(input: Input) {
    setSubmitting(true);
    try {
      await api.post('/auth/mfa/confirm', { email, session, code: input.code });
      await fetchSession();
      toast.success(t('mfa.loginSuccess'));
      navigate('/');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('mfa.failed');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-custel-bg-subtle p-8">
      <div className="w-full max-w-md rounded-custel border border-custel-border bg-custel-surface p-8 shadow-sm">
        <h1 className="mb-2 text-2xl font-semibold text-custel-text">{t('mfa.title')}</h1>
        <p className="mb-6 text-sm text-custel-text-secondary">{t('mfa.subtitle')}</p>

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="code">
              {t('mfa.codeLabel')}
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              className="input-base font-mono text-center text-2xl tracking-widest"
              placeholder="000000"
              autoFocus
              {...register('code')}
            />
            {errors.code && <p className="mt-1 text-xs text-custel-danger">{errors.code.message}</p>}
          </div>

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? t('mfa.verifying') : t('mfa.verify')}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-custel-text-secondary">
          <Link to="/giris" className="underline-offset-2 hover:text-custel-ink hover:underline">
            {t('mfa.backToLogin')}
          </Link>
        </p>
      </div>
    </main>
  );
}
